import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { a as addAccessibility, A as Accessibility } from './accessibility-_zCLcLLO.js';
import { W as WheelGesturesPlugin } from './embla-carousel-wheel-gestures.esm-BxgUSOtT.js';
import { d as debounce } from './debounce-CFN_LA0O.js';
import { DEBOUNCE } from './constants.js';

class KeyFeatureCarousel extends HTMLElement {
  /** @type {import('embla-carousel').EmblaCarouselType | undefined} */
  #embla;
  #screenSize = window.matchMedia('(min-width: 1024px)').matches ? 'desktop' : 'mobile';
  #debouncedReInit = debounce(() => this.#embla?.reInit(this.#options()), DEBOUNCE.DEFAULT);
  #abortController = new AbortController();

  connectedCallback() {
    this.#init();
    this.addEventListener('click', (event) => this.#handleClick(event), {
      signal: this.#abortController.signal,
    });
    // Turn off the re-init from embla and use window resize.
    // This is because the desktop hover will call re-init on cause issues with the carousel.
    window.addEventListener('resize', () => this.#handleResize(), {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#debouncedReInit.cancel();
    this.#embla?.destroy();
    this.#abortController.abort();
  }

  #isDesktop() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  #visibleSlides() {
    return [...this.querySelectorAll('[data-key-feature-slide]')].filter(
      (slide) => window.getComputedStyle(slide).display !== 'none'
    );
  }

  #options() {
    const isDesktop = this.#isDesktop();
    return {
      slides: this.#visibleSlides(),
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: true,
      draggable: true,
      direction: document.dir || 'ltr',
      // Desktop hover expands slide width; Embla resize would fight that.
      // Mobile needs resize so the track can be dragged after layout/images settle.
      resize: !isDesktop,
    };
  }

  #init() {
    const viewport = this.querySelector('[data-key-feature-viewport]');
    if (!viewport) return;

    this.#embla = EmblaCarousel(viewport, this.#options(), [
      Accessibility(),
      WheelGesturesPlugin(),
    ]);

    addAccessibility(this.#embla);
  }

  #handleResize() {
    const isDesktop = this.#isDesktop();
    if (isDesktop && this.#screenSize !== 'desktop') {
      this.#screenSize = 'desktop';
      this.#collapseAllCards();
    } else if (!isDesktop && this.#screenSize === 'desktop') {
      this.#screenSize = 'mobile';
    }
    this.#debouncedReInit();
  }

  #collapseAllCards() {
    this.querySelectorAll('.key-feature-card[data-expanded="true"]').forEach((card) => {
      const toggle = card.querySelector('[data-key-feature-toggle]');
      card.dataset.expanded = 'false';
      if (toggle) toggle.ariaExpanded = 'false';
    });
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const toggle =
      event.target instanceof Element ? event.target.closest('[data-key-feature-toggle]') : null;
    if (!toggle) return;

    const card = toggle.closest('.key-feature-card');
    if (!card) return;

    const expanded = card.getAttribute('data-expanded') === 'true';
    card.dataset.expanded = String(!expanded);
    toggle.ariaExpanded = String(!expanded);
  };
}

customElements.define('key-feature-carousel', KeyFeatureCarousel);
